import React, { MouseEvent } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { SidebarMenuItem } from '../sidebar-menu-item/component';
import { FAIcon } from '../../fa-icon';
import { KebabCase } from '../../../utils/StringUtil';
import { parse } from 'url';
import { onExecuteKeyDown } from '../../../utils/KeyboardUtil';

interface MenuItem {
  icon?: any;
  font_family?: string;
  title: string;
  target_url: string;
  id?: string;
  css_class?: string;
  submenu_items?: MenuItem[];
  showChildren?: boolean;
  new_window?: boolean;
}

interface SidebarMenuGroupProps {
  currentUrl: any;
  frontEndDomain?: string;
  item: MenuItem;
  navigate?: any;
  onSelect: (item: SidebarMenuGroupProps['item'], e: MouseEvent<HTMLAnchorElement>) => void;
  setCurrentUrl: (url: any) => void;
  sidebarExpanded?: boolean;
}

export const SidebarMenuGroup: React.FC<SidebarMenuGroupProps> = ({
  currentUrl,
  frontEndDomain,
  item,
  navigate,
  onSelect,
  setCurrentUrl,
  sidebarExpanded,
}) => {
  const getValidQueryString = (queryString: any) => {
    const queryParams = queryString.replace('?', '').split('&');
    const newSearch =
      '?' +
      queryParams
        .reduce((newQuery: Array<any>, param: string) => {
          const [key] = param.split('=');
          if (key !== 'force') {
            newQuery.push(param);
          }
          return newQuery;
        }, [])
        .join('&');

    return newSearch === '?' ? '' : newSearch;
  };

  const getUrlPath = (urlStr: string) => {
    const urlObj = parse(urlStr);
    return {
      path: urlObj?.pathname + (urlObj.search ? getValidQueryString(urlObj.search) : ''),
      host: urlObj?.host,
    };
  };

  const isSelectedItem = (item: MenuItem) => {
    if (!item.target_url || !currentUrl) return;
    const currentUrlPath = getUrlPath(currentUrl);
    const targetUrlPath = getUrlPath(item.target_url);
    const isExactMatch = currentUrlPath.path === targetUrlPath.path && currentUrlPath.host === targetUrlPath.host;
    const isValidPath = targetUrlPath.path.split('/').filter(sub => sub).length > 1;

    const matchCondition =
      !(currentUrlPath.path.includes('dashboard') || currentUrlPath.path.includes('events')) &&
      currentUrlPath.path.includes(targetUrlPath.path);

    return isExactMatch || (isValidPath && matchCondition);
  };

  const hasSubmenuItems = item && item.submenu_items && item.submenu_items.length > 0;

  const handleSelect = e => {
    if (item.new_window) return;
    onSelect(item, e);
  };

  const getClassName = () => {
    const { showChildren, submenu_items, css_class } = item;
    const containsSelectedItem = submenu_items && submenu_items.some(isSelectedItem);
    const className = ['navgroup'];
    if (isSelectedItem(item)) {
      className.push('selected');
    }
    if (showChildren || containsSelectedItem) {
      className.push('expanded');
    }
    if (css_class) {
      className.push(css_class);
    }
    return className;
  };

  const { id, submenu_items, target_url, title } = item;

  if (!target_url && !hasSubmenuItems) {
    return null;
  }

  const labelIcon = <FAIcon icon={item.icon} />;
  const chevronIcon = hasSubmenuItems && <FAIcon icon="angle-right" additionalClasses="chevron" />;
  const className = getClassName();
  const tooltip = <Tooltip id="tooltip">{title}</Tooltip>;

  const overlay = (
    <OverlayTrigger placement="right" overlay={tooltip}>
      {labelIcon}
    </OverlayTrigger>
  );

  return (
    <div
      className={className.join(' ')}
      key={title}
      data-object-type="sidebar-navigation"
      data-object-action={`sidebar-${KebabCase(title)}-${hasSubmenuItems ? 'expand' : 'select'}`}
    >
      {hasSubmenuItems ? (
        <>
          <div
            id={id}
            className="navgroup-label ui-default"
            onClick={handleSelect}
            onKeyDown={onExecuteKeyDown(handleSelect)}
            tabIndex={0}
          >
            {chevronIcon} {sidebarExpanded ? labelIcon : overlay} {title}
          </div>
          <ul className="navgroup-list ui-default regular">
            {submenu_items?.map(subitem => (
              <SidebarMenuItem
                frontEndDomain={frontEndDomain}
                navigate={navigate}
                item={subitem}
                key={subitem.title}
                onClickLink={setCurrentUrl}
                isSelected={isSelectedItem}
              />
            ))}
          </ul>
        </>
      ) : (
        <a
          aria-current={isSelectedItem(item) ? 'page' : undefined}
          className="navgroup-label ui-default"
          href={item.target_url}
          id={id}
          onClick={handleSelect}
          onKeyDown={onExecuteKeyDown(handleSelect)}
          role="link"
          tabIndex={0}
          {...(item.new_window ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {chevronIcon} {sidebarExpanded ? labelIcon : overlay} {title}
        </a>
      )}
    </div>
  );
};

SidebarMenuGroup.displayName = 'SidebarMenuGroup';
